import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AssistantTurnLiveStepJSON } from "ai-assistant/turn-steps/assistant-turn-live-step-schema";

/**
 * The turn the panel is waiting on, and the latest thing it is doing (#7286).
 *
 * One turn at a time, because the panel sends one at a time. A step for any other turn - one that
 * has finished, or a turn sent from another tab - is ignored: the reply to the request is the only
 * source of the answer, and a late step must never relabel a turn that is no longer running.
 */
export type AssistantTurnProgressState = {
  watchedTurnId?: string;
  sequence: number;
  label?: string;
};

const initialState: AssistantTurnProgressState = { sequence: 0 };

const assistantTurnProgressSlice = createSlice({
  name: "assistantTurnProgress",
  initialState,
  reducers: {
    assistantTurnWatched: (_state, action: PayloadAction<string>): AssistantTurnProgressState => ({
      watchedTurnId: action.payload,
      sequence: 0,
    }),
    /** Only the turn being watched can be let go of, so a slow cleanup cannot end its successor. */
    assistantTurnUnwatched: (state, action: PayloadAction<string>) =>
      state.watchedTurnId === action.payload ? initialState : state,
    assistantTurnStepReceived: (state, action: PayloadAction<AssistantTurnLiveStepJSON>) => {
      const step = action.payload;
      if (step.turnId !== state.watchedTurnId) return;
      if (step.sequence <= state.sequence) return;

      state.sequence = step.sequence;
      state.label = step.label;
    },
  },
});

export const { assistantTurnWatched, assistantTurnUnwatched, assistantTurnStepReceived } =
  assistantTurnProgressSlice.actions;

/** What the given turn is doing, or nothing when it is not the watched turn or has said nothing yet. */
export const selectLiveTurnLabel = (
  state: { assistantTurnProgress: AssistantTurnProgressState },
  turnId: string | undefined,
): string | undefined =>
  turnId !== undefined && state.assistantTurnProgress.watchedTurnId === turnId
    ? state.assistantTurnProgress.label
    : undefined;

export default assistantTurnProgressSlice;
